import type { AttendeeReportResponse, AttendeeCheckInResponse, AttendeeQrCheckInInfo, AttendeeQrCheckInTicketItemInfo } from '@/types/attendee';

interface ModifiedForm {
	[key: string]: string | number | string[];
}

export const transformAttendeeInfo = (data: AttendeeReportResponse) => {
	// Extracting header information for both event and form fields
	const labelMapping = new Map((data.labelMapping || []).map(({ key, columnName }) => [key, columnName]));
	const formLabelMapping = new Map((data.formFieldLabelMapping || []).map(({ key, columnName }) => [key.toString(), columnName]));

	// Combining event and form headers
	const headers = [...labelMapping.values(), ...formLabelMapping.values()];
	const headerKeys = [...labelMapping.keys(), ...formLabelMapping.keys()];

	// Modifying data to transform form field structure
	const modifiedData = (data.attendees || []).map(({ form, ...rest }) => {
		const modifiedObj: ModifiedForm = { ...rest };

		// Processing form fields
		if (form) {
			[...formLabelMapping.keys()].forEach((questionId) => {
				const formField = form.find((question) => question?.fieldId.toString() === questionId);

				// If the question is found, add it to the modified object; otherwise, set an empty string
				modifiedObj[questionId] = formField ? formField.fieldValue : '';
			});
		}
		return modifiedObj;
	});

	// Creating CSV rows from the modified data
	const rows = modifiedData.map((obj) => headerKeys.map((header) => {
		if (header === 'seatsIds') {
			return (obj.seatsIds as string[]).join(' ');
		} else if ([...formLabelMapping.keys()].includes(header)) {
			// Checking for commas or double-quotes in question answers and escaping them for CSV format
			const answer = obj[header] as string;
			return answer.includes(',') || answer.includes('"') ? `"${answer}"` : answer;
		}
		return obj[header];
	}));

	// Generating CSV rows with headers
	const headerRow = `${headers.join(',')}\n`;
	const csvRows = [headerRow, ...rows.map((row) => `${row.join(',')}\n`)];

	return csvRows;
};

export const transformAttendeeQrCheckInInfo = (data: AttendeeCheckInResponse): AttendeeQrCheckInInfo => {
	const ownerName = data.attendees[0].name;
	const groupByTicketMap = new Map();
	// Group by ticket name and count
	data.attendees.forEach((item) => {
		const key = item.ticket.name;
		const previousValue = groupByTicketMap.get(key);
		if (!previousValue) {
			groupByTicketMap.set(key, item.ticket.quantity);
		} else {
			groupByTicketMap.set(key, previousValue + item.ticket.quantity);
		}
	});

	// Convert key-value into object
	const ticketSummary: AttendeeQrCheckInTicketItemInfo[] = Array.from(groupByTicketMap).map(([key, value]) => {
		return {
			name: key,
			total: value,
		};
	});

	return {
		ownerName,
		tickets: ticketSummary,
	};
};
